(function () {
  if (window.__campFacetFiltersLoaded) return;
  window.__campFacetFiltersLoaded = true;

  /**
   * Strip a trailing parenthesised qualifier (e.g. " (Basketball)") from a
   * suburb / Location option value so variants like "Cheltenham" and
   * "Cheltenham (Basketball)" collapse to the same canonical key.
   * @param {unknown} value
   * @returns {string}
   */
  const canonicaliseSuburb = (value) =>
    String(value ?? '')
      .replace(/\s*\([^)]*\)\s*$/, '')
      .trim();

  const initCampFilters = (root) => {
    if (!root || root.dataset.campFiltersInitialised === 'true') return;

    root.dataset.campFiltersInitialised = 'true';

    // Date range input — replaced by the session/week dropdown below.
    // const dateRangeInput = root.querySelector('[data-date-picker]');
    const sessionSelect = root.querySelector('[data-camp-filter-session]');
    const suburbSelect = root.querySelector('[data-camp-filter-suburbs]');
    const suburbPlaceholder = root.dataset.suburbPlaceholder || '';
    const initialCity = root.dataset.initialCity || '';
    const filters = {
      // Search value of the selected session/week option, matched against each
      // camp's variant title downstream. Empty string means "no session filter".
      session: '',
      suburbs: [],
    };

    // Active city + sport context used to narrow the suburb option list.
    // The sport restriction arrives on camp-list:sport-changed as a list of
    // suburbs that still have camps for that sport (computed upstream from
    // the camp cards); null means no sport restriction.
    let currentCity = initialCity;
    /** @type {Set<string>|null} */
    let sportAllowedSuburbs = null;

    // Session dropdown wiring for the applied-filter badges: the placeholder to
    // restore when the session badge is removed, and a search-value -> label
    // map so a badge can show the human-readable option text.
    const sessionDropdown = root.querySelector(
      '.camp-filters__session-dropdown',
    );
    const sessionPlaceholder =
      sessionDropdown
        ?.querySelector('[data-dropdown-label]')
        ?.textContent.trim() || '';
    const sessionLabels = {};
    sessionDropdown?.querySelectorAll('[data-search-term]').forEach((span) => {
      const value = span.getAttribute('data-search-term');
      if (value) sessionLabels[value] = span.textContent.trim();
    });

    // Applied-filter badge carousel at the bottom of the facets.
    const appliedContainer = root.querySelector('[data-camp-filters-applied]');
    const appliedList = root.querySelector('[data-camp-filters-applied-list]');
    /** @type {import('swiper').Swiper | null} */
    let appliedSwiper = null;

    const emitFiltersChanged = () => {
      root.dispatchEvent(
        new CustomEvent('camp-list:filters-changed', {
          bubbles: true,
          detail: {
            ...filters,
            session: filters.session,
            suburbs: [...filters.suburbs],
          },
        }),
      );
      renderAppliedFilters();
    };

    /**
     * Build a single removable badge slide for an applied filter.
     * @param {{type: string, value: string, label: string}} badge
     * @returns {HTMLElement}
     */
    function createBadge({ type, value, label }) {
      const slide = document.createElement('div');
      slide.className = 'swiper-slide !w-fit';

      // Colour-code the pill border by filter type: blue for the week/session
      // filter, orange for suburb filters.
      const borderClass = type === 'session' ? 'border-blue' : 'border-orange';

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `camp-filters__applied-badge inline-flex items-center gap-1 whitespace-nowrap rounded-full border ${borderClass} bg-white px-3 py-1 text-sm leading-none`;
      btn.dataset.removeFilter = '';
      btn.dataset.filterType = type;
      btn.dataset.filterValue = value;
      btn.setAttribute('aria-label', `Remove ${label}`);

      const text = document.createElement('span');
      text.textContent = label;

      const close = document.createElement('span');
      close.setAttribute('aria-hidden', 'true');
      close.className = 'text-base leading-none';
      close.textContent = '×';

      btn.append(text, close);
      slide.append(btn);
      return slide;
    }

    /**
     * Re-render the applied-filter badges from the current `filters` state and
     * (re)initialise the Swiper carousel. Hides the container when empty.
     */
    function renderAppliedFilters() {
      if (!appliedContainer || !appliedList) return;

      const badges = [];
      if (filters.session) {
        badges.push({
          type: 'session',
          value: filters.session,
          label: sessionLabels[filters.session] || filters.session,
        });
      }
      filters.suburbs.forEach((suburb) => {
        badges.push({ type: 'suburb', value: suburb, label: suburb });
      });

      appliedList.replaceChildren(...badges.map(createBadge));

      const hasBadges = badges.length > 0;
      appliedContainer.hidden = !hasBadges;
      appliedContainer.classList.toggle('hidden', !hasBadges);

      if (!hasBadges) return;

      if (typeof Swiper !== 'undefined') {
        if (!appliedSwiper) {
          appliedSwiper = new Swiper(appliedContainer, {
            slidesPerView: 'auto',
            spaceBetween: 8,
            slidesOffsetAfter: 20,
          });
        } else {
          appliedSwiper.update();
        }
      }
    }

    appliedList?.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-remove-filter]');
      if (!btn || !appliedList.contains(btn)) return;
      event.preventDefault();

      const type = btn.dataset.filterType;
      const value = btn.dataset.filterValue;

      if (type === 'session') {
        filters.session = '';
        if (sessionSelect) sessionSelect.value = '';
        const label = sessionDropdown?.querySelector('[data-dropdown-label]');
        if (label) label.textContent = sessionPlaceholder;
      } else if (type === 'suburb') {
        filters.suburbs = filters.suburbs.filter((suburb) => suburb !== value);
        if (suburbMultiSelect?.setSelects) {
          suburbMultiSelect.setSelects(filters.suburbs);
        } else if (suburbSelect) {
          Array.from(suburbSelect.options).forEach((option) => {
            if (option.value === value) option.selected = false;
          });
        }
      }

      emitFiltersChanged();
    });

    sessionSelect?.addEventListener('change', () => {
      filters.session = sessionSelect.value || '';
      emitFiltersChanged();
    });

    // Date range facet wiring — retained for reference, superseded by the
    // session dropdown above.
    // document.addEventListener('camp-list:date-range-changed', (event) => {
    //   const dateRange = event.detail?.dateRange;
    //   filters.dateRange = {
    //     start: dateRange?.start ?? null,
    //     end: dateRange?.end ?? null,
    //   };
    //   emitFiltersChanged();
    // });

    /**
     * @type {import('multiple-select-vanilla').MultipleSelectInstance | null}
     */
    let suburbMultiSelect = null;

    const setFacetsDisabled = (disabled) => {
      root.classList.toggle('camp-filters--disabled', disabled);
      if (sessionSelect) sessionSelect.disabled = disabled;
      if (suburbSelect) suburbSelect.disabled = disabled;
      if (disabled) {
        suburbMultiSelect?.disable?.();
      } else {
        suburbMultiSelect?.enable?.();
      }
    };

    document.addEventListener('camp-list:facets-toggle', (event) => {
      setFacetsDisabled(event.detail?.disabled ?? false);
    });

    // Build a deduplicated list of all suburbs with their city so entries
    // like "Cheltenham" and "Cheltenham (Basketball)" collapse to one, and
    // so we can re-filter client-side when the active city or sport changes.
    const seenCanonicalSuburbs = new Set();
    const allSuburbOptions = [];

    Array.from(suburbSelect?.options ?? []).forEach((option) => {
      const canonical = option.value;
      if (!canonical || seenCanonicalSuburbs.has(canonical)) return;
      seenCanonicalSuburbs.add(canonical);
      allSuburbOptions.push({
        text: canonical,
        value: canonical,
        city: option.dataset.city || '',
      });
    });

    const filterSuburbOptions = () => {
      let options = allSuburbOptions;
      if (currentCity) {
        options = options.filter(
          (opt) =>
            !opt.city || opt.city.toLowerCase() === currentCity.toLowerCase(),
        );
      }
      if (sportAllowedSuburbs) {
        options = options.filter((opt) =>
          sportAllowedSuburbs.has(canonicaliseSuburb(opt.value).toLowerCase()),
        );
      }
      return options;
    };

    if (window.multipleSelect && suburbSelect && !suburbSelect.disabled) {
      suburbMultiSelect = window.multipleSelect(suburbSelect, {
        placeholder: suburbPlaceholder,
        selectAll: false,
        showClear: true,
        filter: true,
        width: '100%',
        onChange: ({ selection }) => {
          filters.suburbs = selection.values.map((value) => String(value));
          emitFiltersChanged();
        },
      });

      const applySuburbOptions = () => {
        const options = filterSuburbOptions().map(({ text, value }) => ({
          text,
          value,
        }));
        suburbMultiSelect.setData(options);
        filters.suburbs = [];
        emitFiltersChanged();
      };

      suburbMultiSelect.setData(
        filterSuburbOptions().map(({ text, value }) => ({ text, value })),
      );

      filters.suburbs = suburbMultiSelect
        .getSelects('value')
        .map((value) => String(value));

      document.addEventListener('camp-list:city-changed', (event) => {
        currentCity = event.detail?.city || '';
        applySuburbOptions();
      });

      document.addEventListener('camp-list:sport-changed', (event) => {
        const allowed = event.detail?.suburbs;
        sportAllowedSuburbs = Array.isArray(allowed)
          ? new Set(
              allowed.map((suburb) => canonicaliseSuburb(suburb).toLowerCase()),
            )
          : null;
        applySuburbOptions();
      });
    }

    // Native <select> fallback for when multiple-select-vanilla isn't loaded.
    // When the plugin IS active it drives virtual data via setData(), so the
    // native <select>'s selectedOptions are not a reliable source of truth —
    // reading them here would clobber filters.suburbs (e.g. setSelects() fires
    // a native `change` without a synced native selection). The plugin's own
    // onChange handler is authoritative in that case.
    if (!suburbMultiSelect) {
      suburbSelect?.addEventListener('change', () => {
        filters.suburbs = Array.from(suburbSelect.selectedOptions).map(
          (option) => option.value,
        );
        emitFiltersChanged();
      });
    }

    // Restore facet state from the URL — dispatched by full-camp-map.js on
    // page load and popstate. Values are matched case-insensitively against
    // the known options (unknown values are dropped), the session dropdown /
    // suburb multi-select UI is updated, and camp-list:filters-changed is
    // re-emitted so downstream listeners re-filter the cards and map.
    document.addEventListener('camp-list:filters-restore', (event) => {
      const detail = event.detail || {};

      const requestedSession = String(detail.session ?? '');
      const sessionKey =
        Object.keys(sessionLabels).find(
          (value) => value.toLowerCase() === requestedSession.toLowerCase(),
        ) || '';
      filters.session = sessionKey;
      if (sessionSelect) sessionSelect.value = sessionKey;
      const sessionLabel = sessionDropdown?.querySelector(
        '[data-dropdown-label]',
      );
      if (sessionLabel) {
        sessionLabel.textContent = sessionKey
          ? sessionLabels[sessionKey]
          : sessionPlaceholder;
      }

      const requestedSuburbs = Array.isArray(detail.suburbs)
        ? detail.suburbs.map(canonicaliseSuburb).filter(Boolean)
        : [];
      const availableValues = filterSuburbOptions().map((opt) => opt.value);
      const matchedSuburbs = requestedSuburbs
        .map((requested) =>
          availableValues.find(
            (value) => value.toLowerCase() === requested.toLowerCase(),
          ),
        )
        .filter(Boolean);

      filters.suburbs = matchedSuburbs;
      if (suburbMultiSelect?.setSelects) {
        suburbMultiSelect.setSelects(matchedSuburbs);
      } else if (suburbSelect) {
        Array.from(suburbSelect.options).forEach((option) => {
          option.selected = matchedSuburbs.includes(option.value);
        });
      }

      emitFiltersChanged();
    });

    // Reflect any initial filter state (and set the container's hidden state).
    renderAppliedFilters();
  };

  const initAll = () => {
    document.querySelectorAll('[data-camp-filters]').forEach(initCampFilters);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
})();
